// CORE
import { useAppSelector, useAppDispatch, useAppStore } from 'hooks/redux';
import React, { useMemo } from 'react';

// DATA
import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';
import { useLogoForBiller } from 'app-components/flux/bill-presentment/billPresentmentHooks';
import { useLogoForTxnOrModel } from 'app-components/flux/domain-logos/domainLogosHooks';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import { institutionLoginsSelectors } from 'app-components/flux/institution-logins';
import {
  scheduledTransactionsActions,
  scheduledTransactionsSelectors,
  scheduledTransactionsUtils,
} from 'app-components/flux/scheduled-transactions';
import {
  AutoAdjustAmountEnum,
  type ScheduledTransactionType,
  mkScheduledTransactionPatch,
} from 'app-components/flux/scheduled-transactions/scheduledTransactionsTypes';
import { transactionsSelectors, transactionsUtils } from 'app-components/flux/transactions';
import {
  type TransactionProps,
  type TransactionRecord,
  mkCashFlowTransactionPatch,
} from 'app-components/flux/transactions/transactionsTypes';

// MUI
import Avatar from '@mui/material/Avatar';
import { alpha, useTheme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EditIcon from '@mui/icons-material/Edit';

import openSelectLogoDialog from 'components/Dialogs/SelectLogoDialog/dispatcher';
import SeriesIcon, { getSeriesIconKind, type SeriesIconKind, type SeriesIconSize } from 'images/icons/SeriesIcon';
import { buttonFocusVisibleStyles } from 'themes/muiComponents/buttonFocusTokens';

import ConnectedIcon from './ConnectedIcon';

const SERIES_ICON_SIZE_BY_AVATAR_SIZE: Array<{ max: number; size: SeriesIconSize }> = [
  { max: 24, size: 'sm' },
  { max: 40, size: 'md' },
  { max: 56, size: 'lg' },
  { max: Number.POSITIVE_INFINITY, size: 'xl' },
];

const getSeriesIconSize = (avatarSize: number): SeriesIconSize =>
  SERIES_ICON_SIZE_BY_AVATAR_SIZE.find(({ max }) => avatarSize <= max)?.size ?? 'xl';

const useStyles = makeStyles<{ size: number; avatarColor: string; textColor?: string }>()(
  (theme, { size, avatarColor, textColor }) => ({
    avatar: {
      width: size,
      height: size,
      backgroundColor: avatarColor || theme.palette.background.layer2,
      color: textColor || theme.palette.common.white,
      borderRadius: size / 4,
      position: 'relative',
      overflow: 'visible',
      flexGrow: 0,

      fontSize: 24 + Math.round((size - 48) / (size > 48 ? 4 : 2.5)),
      fontWeight: (4 + Math.round(size / 20)) * 100,
    },
    seriesIconAvatar: {
      backgroundColor: 'transparent',
    },
    logoAvatar: {
      // border in case of white "floating" logos
      border: `1px solid ${theme.palette.border.light}`,
      boxSizing: 'border-box',
      // no background in case of transparent logos
      backgroundColor: theme.applyOpacityToHex?.(theme.palette.background.paper, 0),
      color: theme.applyOpacityToHex?.(theme.palette.background.paper, 0),
    },
    editable: {
      // Keyboard focus reveals the same edit affordance as hover.
      '&:hover, &:focus-visible': {
        cursor: 'pointer',
        border: `1px solid ${theme.palette.primary.main}`,
        "[data-avatar-mask='true']": {
          opacity: 1,
        },
        "[data-edit-icon='true']": {
          display: 'block',
        },
      },
      '&:focus-visible': buttonFocusVisibleStyles(theme),
    },
    img: {
      display: 'block',
      width: '100%',
      height: '100%',
      borderRadius: size / 4,
    },
    avatarMask: {
      position: 'absolute',
      inset: 0,
      borderRadius: 'inherit',
      backgroundColor: alpha(theme.palette.common.black, 0.2),
      opacity: 0,
      pointerEvents: 'none',
      transition: theme.transitions.create('opacity', {
        duration: theme.transitions.duration.shorter,
      }),
      zIndex: 1,
    },
    editIcon: {
      display: 'none',
      position: 'absolute',
      top: 4,
      right: 4,
      width: (size - 8) / 2,
      height: (size - 8) / 2,
      padding: size / 10,
      borderRadius: '50%',
      color: theme.palette.primary.main,
      backgroundColor: theme.applyOpacityToHex?.(theme.palette.background.paper, 0.74),
      zIndex: 2,
    },
  }),
);

interface ConnectedAvatarProps {
  model?: ScheduledTransactionType;
  /**
   * Opt in to the Bills & Income SeriesIcon fallback; omitted callers retain the legacy text
   * fallback. The refund/income/bill precedence is derived here via `getSeriesIconKind`, so it
   * lives in one place instead of being re-derived by every caller. Ignored when `kind` is set.
   */
  seriesIcon?: boolean;
  /**
   * Escape hatch for callers with a refund rule that differs from the default (e.g. only
   * showing the refund glyph while the refund is still incomplete). Overrides `seriesIcon`.
   */
  kind?: SeriesIconKind;
  txn?: TransactionProps | TransactionRecord;
  size?: number;
  iconSize?: number;
  color?: string;
  textColor?: string;
  className?: string;
  isCurrentCategoryIncome?: boolean;
  editable?: boolean;
  ariaLabel?: string;
  /**
   * Intercepts an icon selection instead of dispatching `updateScheduledTransaction` — for
   * callers editing a draft that hasn't been persisted yet (e.g. Create Series), so the icon
   * rides along in the eventual create/update payload rather than firing its own discrete PATCH.
   */
  onIconChange?: (props: { logoId?: string | null; logoUrl?: string | null }) => void;
}
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
// ### - - -                  Main Component                     - - - ###
// ### - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - ###
function ConnectedAvatar(props: ConnectedAvatarProps) {
  const {
    model,
    seriesIcon,
    kind: kindOverride,
    size = 40,
    txn,
    iconSize = 20,
    color,
    className,
    textColor,
    isCurrentCategoryIncome,
    editable,
    ariaLabel,
    onIconChange,
  } = props;

  const theme = useTheme();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const showIcon = model?.presentmentAccountId;
  let logo: string | undefined;
  let logoName: string | undefined;

  const allowSeriesLogos = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'logosSeriesRelease'));

  const fiBiller = useAppSelector((state) =>
    billPresentmentSelectors.selectBillerForAccountId(state, model?.presentmentAccountId || ''),
  );
  const billerLogo = useLogoForBiller(fiBiller);
  if (model?.presentmentAccountId && billerLogo) {
    logo = billerLogo?.logoUrl;
    logoName = fiBiller?.name;
  }

  // BVA stuff
  const displayTxn = useMemo(() => txn || model?.transaction || ({} as TransactionProps), [txn, model]);

  const creditAccount = useAppSelector((state) => transactionsSelectors.getCreditAccountFromTxn(state, displayTxn));
  const institution = useAppSelector((state) =>
    institutionLoginsSelectors.getInstitutionForInstitutionLogin(state, creditAccount?.institutionLoginId || ''),
  );

  const { autoAdjustAmount, autoAdjustDueOn } = model || {};

  const hasBVA = Boolean(
    creditAccount &&
      transactionsUtils.isTransferTxn(displayTxn) &&
      ((autoAdjustAmount && autoAdjustAmount !== AutoAdjustAmountEnum.none) || autoAdjustDueOn),
  );

  if (displayTxn && institution && transactionsUtils.isTransferTxn(displayTxn) && creditAccount) {
    if (institution?.logoUrl) {
      logo = institution.logoUrl;
      logoName = institution.name;
    } else if (institution?.logo) {
      logo = `data:image/svg;base64, ${institution.logo}`;
      logoName = institution.name;
    }
  }

  const domainLogo = useLogoForTxnOrModel(txn, model, true, !!logo);

  const text = useMemo(
    () =>
      displayTxn.type === 'INVESTMENT'
        ? (
            (displayTxn.investmentTxnType &&
              transactionsUtils.InvestmentTxnActionsByType[displayTxn.investmentTxnType]) ||
            '?'
          ).toUpperCase?.()[0]
        : (displayTxn.payee || '?').toUpperCase?.()[0],
    [displayTxn],
  );

  const isIncomeAvatar = useAppSelector((state) => {
    if (model?.transaction) {
      return scheduledTransactionsSelectors.isIncomeSeriesTxn(state, model.transaction);
    }
    return transactionsSelectors.isIncomeTxn(state, displayTxn);
  });
  const isIncome = isCurrentCategoryIncome === undefined ? isIncomeAvatar : isCurrentCategoryIncome;
  let avatarColor = color || (isIncome ? theme.palette.legend.income : theme.palette.legend.bill);
  if (model?.isCompleted) avatarColor = theme.palette.icon.light;

  const kind =
    kindOverride ?? (seriesIcon ? getSeriesIconKind(transactionsUtils.isRefundTxn(displayTxn), isIncome) : undefined);

  const { classes, cx } = useStyles({ size, avatarColor, textColor });
  const seriesIconSize = getSeriesIconSize(size);

  // Use biller/institution logo, then domain logo, then the series-type icon.
  const finalLogo = logo || domainLogo?.logoUrl || undefined;
  const finalLogoName = logoName || displayTxn?.payee || undefined;

  // A merchant-managed logo (merchantId + logoId) is never customizable.
  const customizable = !logo && editable && allowSeriesLogos && scheduledTransactionsUtils.isSeriesLogoEditable(model);

  const onEditIcon = () => {
    openSelectLogoDialog()({
      currentIcon: domainLogo?.domain || displayTxn.logoUrl || undefined,
      currentLogoId: domainLogo?.id || displayTxn.logoId,
      payeeName: displayTxn.payee || undefined,
      type: isIncome ? 'income' : 'bill',
      onSelectIcon: ({ logoId, logoUrl }) => {
        // The dialog can outlive a sync that locks the logo, so recheck the stored series.
        const liveModel = model?.id
          ? scheduledTransactionsSelectors.getScheduledTransactionById(store.getState(), model.id)
          : undefined;
        if (!scheduledTransactionsUtils.isSeriesLogoEditable(liveModel ?? model)) return;
        if (onIconChange) {
          onIconChange({ logoId, logoUrl });
        } else if (model) {
          dispatch(
            scheduledTransactionsActions.updateScheduledTransaction(
              mkScheduledTransactionPatch({
                id: model.id,
                transaction: mkCashFlowTransactionPatch({ logoId, logoUrl }),
              }),
              { undo: { userMessage: 'Series updated' } },
            ),
          );
        }
      },
    });
  };

  return (
    <Avatar
      aria-label={ariaLabel || (customizable ? `Add logo for ${displayTxn.payee || 'payee'}` : undefined)}
      role={customizable ? 'button' : undefined}
      tabIndex={customizable ? 0 : undefined}
      className={cx(
        classes.avatar,
        className,
        finalLogo ? classes.logoAvatar : kind && classes.seriesIconAvatar,
        customizable && classes.editable,
      )}
      onClick={customizable ? onEditIcon : undefined}
      onKeyDown={
        customizable
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onEditIcon();
              }
            }
          : undefined
      }
    >
      {finalLogo ? (
        <img alt={finalLogoName || 'logo'} src={finalLogo} className={classes.img} />
      ) : kind ? (
        <SeriesIcon kind={kind} size={seriesIconSize} />
      ) : (
        text
      )}

      {customizable && <span className={classes.avatarMask} data-avatar-mask='true' />}
      {customizable && <EditIcon className={classes.editIcon} data-edit-icon='true' />}
      {(showIcon || hasBVA) && <ConnectedIcon model={model} size={iconSize} txn={txn} hasBVA={hasBVA} />}
    </Avatar>
  );
}

ConnectedAvatar.displayName = 'ConnectedAvatar';

export default ConnectedAvatar;
